import { Location, isPlatformBrowser } from "@angular/common";
import { ApplicationRef, ComponentRef, createComponent, Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { ActivatedRoute, Router, NavigationEnd } from "@angular/router";
import { AppActionTypes } from "@app/store/actions/app.actions";
import { HideFullscreenImage } from "@app/store/actions/fullscreen-image.actions";
import { DeleteImageSuccess } from "@app/store/actions/image.actions";
import { MainState } from "@app/store/state";
import { ImageInterface, ImageRevisionInterface, FINAL_REVISION_LABEL } from "@core/interfaces/image.interface";
import { BaseService } from "@core/services/base.service";
import { DeviceService } from "@core/services/device.service";
import { ImageService } from "@core/services/image/image.service";
import { LoadingService } from "@core/services/loading.service";
import { TitleService } from "@core/services/title/title.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ImageViewerSlideshowComponent } from "@shared/components/misc/image-viewer-slideshow/image-viewer-slideshow.component";
import {
  PresentationModeComponent,
  PresentationModeSettings
} from "@shared/components/misc/image-viewer-slideshow/presentation-mode/presentation-mode.component";
import { CookieService } from "ngx-cookie";
import { Subscription, EMPTY, Observable, Subject, BehaviorSubject } from "rxjs";
import { map, take, takeUntil } from "rxjs/operators";

export const SHOW_ANNOTATIONS_ON_MOUSE_HOVER_COOKIE = "astrobin-images-show-annotations-on-mouse-hover";
export const PRESENTATION_MODE_SETTINGS_KEY = "astrobin-presentation-mode-settings";

export interface ImageViewerNavigationContextItem {
  imageId: ImageInterface["hash"] | ImageInterface["pk"];
  thumbnailUrl: string;
  image?: ImageInterface;
}

export type ImageViewerNavigationContext = ImageViewerNavigationContextItem[];

export interface PresentationModeState {
  isActive: boolean;
  isPaused: boolean;
  settings: PresentationModeSettings;
}

@Injectable({
  providedIn: "root"
})
export class ImageViewerService extends BaseService {
  slideshow: ComponentRef<ImageViewerSlideshowComponent>;
  presentationMode: ComponentRef<PresentationModeComponent>;

  public slideshowState$: Observable<boolean>;
  public presentationModeState$: Observable<PresentationModeState>;
  public presentationModeNearEndOfContext$: Observable<string>;
  public showAnnotationsOnMouseHover = false;

  private readonly _isBrowser: boolean;
  private _previousTitle: string;
  private _previousDescription: string;
  private _previousUrl: string;
  private _slideshowStateSubject = new Subject<boolean>();
  private _presentationModeStateSubject = new BehaviorSubject<PresentationModeState>({
    isActive: false,
    isPaused: false,
    settings: this._getDefaultPresentationSettings()
  });
  private _presentationModeNearEndOfContextSubject = new Subject<string>();
  private _routerEventsSubscription: Subscription;

  constructor(
    public readonly loadingService: LoadingService,
    public readonly store$: Store<MainState>,
    public readonly actions$: Actions,
    @Inject(PLATFORM_ID) public readonly platformId: Object,
    public readonly windowRefService: WindowRefService,
    public readonly location: Location,
    public readonly deviceService: DeviceService,
    public readonly router: Router,
    public readonly titleService: TitleService,
    public readonly imageService: ImageService,
    public readonly applicationRef: ApplicationRef,
    public readonly utilsService: UtilsService,
    public readonly cookieService: CookieService
  ) {
    super(loadingService);

    this.slideshowState$ = this._slideshowStateSubject.asObservable();
    this.presentationModeState$ = this._presentationModeStateSubject.asObservable();
    this.presentationModeNearEndOfContext$ = this._presentationModeNearEndOfContextSubject.asObservable();

    this._isBrowser = isPlatformBrowser(platformId);

    this.actions$
      .pipe(
        ofType(AppActionTypes.DELETE_IMAGE_SUCCESS),
        takeUntil(this.destroyed$),
        map((action: DeleteImageSuccess) => action.payload.pk)
      )
      .subscribe((pk: ImageInterface["pk"]) => {
        if (this.slideshow && this.slideshow.instance.activeImage.pk === pk) {
          this.closeSlideShow(true);
        }
        if (this.presentationMode && this.presentationMode.instance.currentImage?.pk === pk) {
          this.closePresentationMode();
        }
      });

    this._initShowAnnotationsOnMouseHover();
    this._loadPresentationSettings();
  }

  autoOpenSlideshow(callerComponentId: string, activatedRoute: ActivatedRoute): void {
    const queryParams = activatedRoute.snapshot.queryParams;

    if (queryParams["i"]) {
      const currentImageId = this.slideshow?.instance?.activeId;

      if (currentImageId && currentImageId === queryParams["i"]) {
        return;
      }

      this.openSlideshow(
        callerComponentId,
        queryParams["i"],
        queryParams["r"] || FINAL_REVISION_LABEL,
        [],
        true
      ).subscribe();
    }
  }

  openSlideshow(
    callerComponentId: string,
    imageId: ImageInterface["hash"] | ImageInterface["pk"],
    revisionLabel: ImageRevisionInterface["label"],
    navigationContext: ImageViewerNavigationContext,
    pushState: boolean
  ): Observable<ComponentRef<ImageViewerSlideshowComponent>> {
    if (!this._isBrowser) {
      return EMPTY;
    }

    return new Observable(observer => {
      if (!this.slideshow) {
        this._previousTitle = this.titleService.getTitle();
        this._previousDescription = this.titleService.getDescription();
        this._previousUrl = this.windowRefService.getCurrentUrl().toString();

        this.slideshow = createComponent(ImageViewerSlideshowComponent, {
          environmentInjector: this.applicationRef.injector
        });

        this.utilsService.delay(100).subscribe(() => {
          if (!this._routerEventsSubscription) {
            this._routerEventsSubscription = this.router.events
              .pipe(
                takeUntil(this.slideshow.instance.closeSlideshow) // Unsubscribe when slideshow closes
              )
              .subscribe(event => {
                if (event instanceof NavigationEnd) {
                  this.closeSlideShow(false);
                }
              });
          }
        });

        if (this._isBrowser) {
          const body = this.windowRefService.nativeWindow.document.body;

          // Create and add black overlay
          const overlay = this.windowRefService.nativeWindow.document.createElement("div");
          overlay.id = "image-viewer-overlay";
          overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: black;
            z-index: 1045;
            pointer-events: none;
          `;
          body.appendChild(overlay);

          body.appendChild(this.slideshow.location.nativeElement);
          body.classList.add("image-viewer-open");
        }

        this.applicationRef.attachView(this.slideshow.hostView);

        this._slideshowStateSubject.next(true);
        this._stopBodyScrolling();

        this.slideshow.instance.closeSlideshow.pipe(take(1)).subscribe(pushState => {
          this.closeSlideShow(pushState);
          this.titleService.setTitle(this._previousTitle);
          this.titleService.setDescription(this._previousDescription);
          this.titleService.updateMetaTag({ property: "og:url", content: this._previousUrl });
        });

        this.slideshow.instance.imageChange.subscribe((image: ImageInterface) => {
          if (this._isBrowser) {
            const currentUrl = this.windowRefService.getCurrentUrl();
            const currentImageParam = currentUrl.searchParams.get("i");
            const newImageId = image.hash || image.pk.toString();

            // Only update URL if the image ID has actually changed
            if (currentImageParam !== newImageId) {
              let url = currentUrl.href;
              url = UtilsService.addOrUpdateUrlParam(url, "i", newImageId);
              this.windowRefService.pushState({ imageId: newImageId }, url);
            }
          }
        });
      }

      this.slideshow.instance.setCallerComponentId(callerComponentId);
      this.slideshow.instance.setNavigationContext(navigationContext);
      this.slideshow.instance.setImage(imageId, revisionLabel, pushState).subscribe({
        next: () => {
          observer.next(this.slideshow);
          observer.complete();
        },
        error: () => {
          this.closeSlideShow(false);
          observer.error();
        }
      });
    });
  }

  closeSlideShow(pushState: boolean): void {
    if (this.slideshow) {
      this.slideshow.location.nativeElement.remove();
      this.slideshow.destroy();
      this.slideshow = null;

      if (this._routerEventsSubscription) {
        this._routerEventsSubscription.unsubscribe();
        this._routerEventsSubscription = null;
      }

      if (this._isBrowser) {
        const body = this.windowRefService.nativeWindow.document.body;
        body.classList.remove("image-viewer-open");

        // Remove the black overlay
        const overlay = this.windowRefService.nativeWindow.document.getElementById("image-viewer-overlay");
        if (overlay) {
          overlay.remove();
        }
      }

      this._slideshowStateSubject.next(false);

      this.store$.dispatch(new HideFullscreenImage());
      this._resumeBodyScrolling();

      if (pushState) {
        const currentUrl = this.windowRefService.getCurrentUrl();
        let url = UtilsService.removeUrlParam(currentUrl.href, "i");
        url = UtilsService.removeUrlParam(url, "r");
        url = UtilsService.removeUrlParam(url, "annotations");
        url = UtilsService.removeUrlParam(url, "measurements");

        // Preserve the fragment if it exists
        if (currentUrl.hash) {
          url += currentUrl.hash;
        }

        this.windowRefService.replaceState({}, url);
      }
    }
  }

  isSlideshowOpen(): boolean {
    return !!this.slideshow;
  }

  getScrollArea(imageId: ImageInterface["hash"] | ImageInterface["pk"]): {
    scrollArea: HTMLElement;
    windowWidth: number;
    windowHeight: number;
    viewPortAspectRatio: number;
    sideToSideLayout: boolean;
  } | null {
    let scrollArea: HTMLElement;

    const windowWidth = this.windowRefService.nativeWindow.innerWidth;
    const windowHeight = this.windowRefService.nativeWindow.innerHeight;
    const viewPortAspectRatio = windowWidth / windowHeight;
    const sideToSideLayout = this.deviceService.lgMin() || viewPortAspectRatio > 1;

    if (sideToSideLayout) {
      scrollArea = this.windowRefService.nativeWindow.document.querySelector(
        `#image-viewer-${imageId} > .main-area-container > .main-area > .data-area-container > .data-area`
      );
    } else {
      scrollArea = this.windowRefService.nativeWindow.document.querySelector(
        `#image-viewer-${imageId} > .main-area-container > .main-area`
      );
    }

    return {
      scrollArea,
      windowWidth,
      windowHeight,
      viewPortAspectRatio,
      sideToSideLayout
    };
  }

  toggleShowAnnotationsOnMouseHover(): void {
    this.showAnnotationsOnMouseHover = !this.showAnnotationsOnMouseHover;
    this.cookieService.put(SHOW_ANNOTATIONS_ON_MOUSE_HOVER_COOKIE, this.showAnnotationsOnMouseHover.toString());
  }

  private _stopBodyScrolling(): void {
    this.windowRefService.changeBodyOverflow("hidden");
  }

  private _resumeBodyScrolling(): void {
    this.windowRefService.changeBodyOverflow("auto");
  }

  openPresentationMode(
    navigationContext: ImageViewerNavigationContext,
    startIndex: number,
    callerComponentId?: string
  ): void {
    console.log("[ImageViewerService] openPresentationMode called", {
      navigationContext,
      startIndex,
      isBrowser: this._isBrowser,
      hasPresentationMode: !!this.presentationMode
    });

    if (!this._isBrowser || this.presentationMode) {
      console.log("[ImageViewerService] Exiting early - not browser or already has presentation mode");
      return;
    }

    console.log("[ImageViewerService] Creating PresentationModeComponent");
    this.presentationMode = createComponent(PresentationModeComponent, {
      environmentInjector: this.applicationRef.injector
    });

    const currentState = this._presentationModeStateSubject.value;
    console.log("[ImageViewerService] Current settings:", currentState.settings);

    this.presentationMode.instance.navigationContext = navigationContext;
    this.presentationMode.instance.currentIndex = startIndex;
    this.presentationMode.instance.settings = currentState.settings;
    this.presentationMode.instance.callerComponentId = callerComponentId;

    this.presentationMode.instance.close.pipe(take(1)).subscribe(() => {
      this.closePresentationMode();
    });

    this.presentationMode.instance.settingsChanged.subscribe((settings: PresentationModeSettings) => {
      this.updatePresentationSettings(settings);
    });

    this.presentationMode.instance.nearEndOfContext.subscribe((componentId: string) => {
      this._presentationModeNearEndOfContextSubject.next(componentId);
    });

    if (this._isBrowser) {
      console.log("[ImageViewerService] Appending presentation mode to DOM");
      const body = this.windowRefService.nativeWindow.document.body;
      body.appendChild(this.presentationMode.location.nativeElement);
      body.classList.add("presentation-mode-open");
    }

    console.log("[ImageViewerService] Attaching view to Angular");
    this.applicationRef.attachView(this.presentationMode.hostView);

    this._presentationModeStateSubject.next({
      isActive: true,
      isPaused: false,
      settings: currentState.settings
    });

    console.log("[ImageViewerService] Presentation mode opened successfully");
  }

  closePresentationMode(): void {
    if (this.presentationMode) {
      this.presentationMode.location.nativeElement.remove();
      this.presentationMode.destroy();
      this.presentationMode = null;

      if (this._isBrowser) {
        const body = this.windowRefService.nativeWindow.document.body;
        body.classList.remove("presentation-mode-open");
      }

      const currentState = this._presentationModeStateSubject.value;
      this._presentationModeStateSubject.next({
        ...currentState,
        isActive: false,
        isPaused: false
      });
    }
  }

  updatePresentationSettings(settings: Partial<PresentationModeSettings>): void {
    const currentState = this._presentationModeStateSubject.value;
    const newSettings = { ...currentState.settings, ...settings };

    this._presentationModeStateSubject.next({
      ...currentState,
      settings: newSettings
    });

    if (this._isBrowser) {
      localStorage.setItem(PRESENTATION_MODE_SETTINGS_KEY, JSON.stringify(newSettings));
    }
  }

  updatePresentationModeNavigationContext(navigationContext: ImageViewerNavigationContext): void {
    if (this.presentationMode && this.presentationMode.instance) {
      console.log("[ImageViewerService] Updating presentation mode navigation context");
      this.presentationMode.instance.navigationContext = navigationContext;
    }
  }

  updateNavigationContext(navigationContext: ImageViewerNavigationContext): void {
    // Update slideshow if it exists
    if (this.slideshow && this.slideshow.instance) {
      console.log("[ImageViewerService] Updating slideshow navigation context");
      this.slideshow.instance.setNavigationContext(navigationContext);
    }

    // Update presentation mode if it exists
    if (this.presentationMode && this.presentationMode.instance) {
      console.log("[ImageViewerService] Updating presentation mode navigation context");
      this.presentationMode.instance.navigationContext = navigationContext;
    }
  }

  private _getDefaultPresentationSettings(): PresentationModeSettings {
    return {
      playbackSpeed: 10,
      animationSpeed: 1000,
      animationType: "fade",
      viewMode: "dynamic",
      autoHideControlsDelay: 3000,
      blurredBackground: true
    };
  }

  private _loadPresentationSettings(): void {
    if (this._isBrowser) {
      const storedSettings = localStorage.getItem(PRESENTATION_MODE_SETTINGS_KEY);
      if (storedSettings) {
        try {
          const settings = JSON.parse(storedSettings);
          const currentState = this._presentationModeStateSubject.value;
          this._presentationModeStateSubject.next({
            ...currentState,
            settings: { ...this._getDefaultPresentationSettings(), ...settings }
          });
        } catch (e) {
          console.error("Failed to load presentation settings", e);
        }
      }
    }
  }

  private _initShowAnnotationsOnMouseHover(): void {
    const showAnnotationsOnMouseHoverCookie = this.cookieService.get(SHOW_ANNOTATIONS_ON_MOUSE_HOVER_COOKIE);

    this.showAnnotationsOnMouseHover =
      showAnnotationsOnMouseHoverCookie === "true" ||
      showAnnotationsOnMouseHoverCookie === null ||
      showAnnotationsOnMouseHoverCookie === undefined;
  }
}
